// BootScene: genera texturas procedurales (todo el arte se dibuja en código)
// y precarga los fondos cósmicos. Espera a que las fuentes estén listas antes del menú.

import Phaser from 'phaser';
import { HEX } from '../config';
import { MISSION_SHUTTLE_OPEN_DATA, MISSION_SHUTTLE_CLOSED_DATA } from '../assets/missionShuttle';
import { AVATAR_CATALOG } from '../avatar/AvatarCatalog';

export class BootScene extends Phaser.Scene {
  constructor() {
    super('BootScene');
  }

  preload() {
    this.load.image('mission_shuttle_open', MISSION_SHUTTLE_OPEN_DATA);
    this.load.image('mission_shuttle_closed', MISSION_SHUTTLE_CLOSED_DATA);
    this.load.image('bg_deepspace', '/assets/bg_deepspace.jpg');
    this.load.image('bg_planet', '/assets/bg_planet.jpg');
    this.load.image('bg_resta_day', '/assets/bg_resta_day.jpg');
    this.load.image('bg_multiplicacion_forest', '/assets/bg_multiplicacion_forest.webp');
    this.load.image('bg_division_ocean', '/assets/bg_division_ocean.webp');
    this.load.image('player_official', '/assets/player_explorador_hd.svg');
    this.load.image('player_exploradora', '/assets/player_exploradora_hd.svg');
    this.load.image('player_official_run', '/assets/player_explorador_run.svg');
    this.load.image('player_exploradora_run', '/assets/player_exploradora_run.svg');
    AVATAR_CATALOG.bases.forEach(base => {
      this.load.image(`portrait_${base.id}`, base.portraitAsset);
    });
  }

  create() {
    this.makePlatform();
    this.makeRestaPlatform();
    this.makeDivisionPlatform();
    this.makePlayer();
    this.makeCrystal();
    this.makeRestaCrystal();
    this.makeDivisionCrystal();
    this.makeStar();
    this.makeSpark();
    this.makeGlow();
    this.makePortal();
    this.makeRestaPortal();
    this.makeMultiplicationPortal();
    this.makeDivisionPortal();

    const start = () => this.scene.start('MenuScene');
    if (document.fonts && document.fonts.ready) {
      Promise.all([
        document.fonts.load('700 40px Orbitron'),
        document.fonts.load('800 40px "Baloo 2"'),
      ])
        .catch(() => {})
        .finally(() => document.fonts.ready.then(start).catch(start));
    } else {
      start();
    }
  }

  tex(key, w, h, draw) {
    const g = this.add.graphics();
    draw(g);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  makePlatform() {
    const w = 240;
    const h = 64;
    this.tex('platform', w, h, (g) => {
      // Cuerpo de roca-tecno oscura con borde neón superior.
      g.fillStyle(0x241a52, 1);
      g.fillRoundedRect(0, 6, w, h - 6, 16);
      g.fillStyle(0x160f38, 1);
      g.fillRoundedRect(6, 20, w - 12, h - 22, 12);
      // Franja luminosa superior.
      g.fillStyle(HEX.cyan, 1);
      g.fillRoundedRect(0, 0, w, 12, 6);
      g.fillStyle(HEX.white, 0.5);
      g.fillRoundedRect(10, 2, w - 20, 4, 2);
      // Detalles de circuito.
      g.fillStyle(HEX.violet, 0.5);
      for (let i = 0; i < 5; i++) g.fillRect(24 + i * 44, 34, 6, 6);
    });
  }

  makeRestaPlatform() {
    const w = 240;
    const h = 64;
    this.tex('platform_resta', w, h, (g) => {
      // Piedra clara del planeta diurno, con vegetación y medallones de resta.
      g.fillStyle(0xe8dcc0, 1);
      g.fillRoundedRect(0, 8, w, h - 8, 14);
      g.fillStyle(0xc8b890, 1);
      g.fillRoundedRect(6, 28, w - 12, h - 30, 10);
      g.fillStyle(0x63c96b, 1);
      g.fillRoundedRect(0, 0, w, 12, 6);
      g.fillStyle(0xb8f08d, 0.9);
      g.fillRoundedRect(10, 2, w - 20, 4, 2);
      g.fillStyle(0xf5edd9, 0.95);
      for (let i = 0; i < 4; i++) {
        const x = 38 + i * 54;
        g.fillCircle(x, 42, 10);
        g.fillStyle(0x6f806f, 1);
        g.fillRoundedRect(x - 6, 40, 12, 4, 2);
        g.fillStyle(0xf5edd9, 0.95);
      }
    });
  }

  makePlayer() {
    const w = 52;
    const h = 66;
    this.tex('player', w, h, (g) => {
      // Mochila-propulsor.
      g.fillStyle(0x2a1f66, 1);
      g.fillRoundedRect(6, 22, 12, 30, 5);
      // Cuerpo del traje.
      g.fillStyle(0x1de0d0, 1);
      g.fillRoundedRect(12, 20, 30, 34, 12);
      g.fillStyle(0x0fb8ab, 1);
      g.fillRoundedRect(12, 40, 30, 14, 10);
      // Casco.
      g.fillStyle(0xf2f6ff, 1);
      g.fillCircle(27, 20, 17);
      // Visor.
      g.fillStyle(0x121233, 1);
      g.fillRoundedRect(18, 12, 22, 15, 8);
      g.fillStyle(HEX.cyan, 1);
      g.fillRoundedRect(21, 14, 9, 6, 3);
      g.fillStyle(HEX.magenta, 0.9);
      g.fillRoundedRect(31, 15, 5, 4, 2);
      // Antena.
      g.fillStyle(0xf2f6ff, 1);
      g.fillRect(26, 0, 3, 6);
      g.fillStyle(HEX.gold, 1);
      g.fillCircle(27, 1, 3);
      // Piernas.
      g.fillStyle(0x2a1f66, 1);
      g.fillRoundedRect(15, 52, 10, 14, 4);
      g.fillRoundedRect(29, 52, 10, 14, 4);
      // Luces del propulsor.
      g.fillStyle(HEX.gold, 1);
      g.fillCircle(10, 52, 3);
    });
  }

  makeCrystal() {
    const w = 44;
    const h = 60;
    this.tex('crystal', w, h, (g) => {
      g.fillStyle(HEX.cyan, 0.28);
      g.fillCircle(w / 2, h / 2, 26);
      g.fillStyle(0x9bfff5, 1);
      g.beginPath();
      g.moveTo(22, 2);
      g.lineTo(40, 24);
      g.lineTo(22, 58);
      g.lineTo(4, 24);
      g.closePath();
      g.fillPath();
      g.fillStyle(HEX.white, 0.85);
      g.beginPath();
      g.moveTo(22, 2);
      g.lineTo(31, 24);
      g.lineTo(22, 34);
      g.lineTo(13, 24);
      g.closePath();
      g.fillPath();
    });
  }

  makeRestaCrystal() {
    const w = 58;
    const h = 64;
    this.tex('crystal_resta', w, h, (g) => {
      g.fillStyle(0xffffff, 0.18);
      g.fillCircle(w / 2, 31, 28);
      g.fillStyle(0x76e9ff, 1);
      g.beginPath();
      g.moveTo(29, 2);
      g.lineTo(54, 54);
      g.lineTo(4, 54);
      g.closePath();
      g.fillPath();
      g.fillStyle(0xd9fbff, 0.92);
      g.beginPath();
      g.moveTo(29, 2);
      g.lineTo(29, 54);
      g.lineTo(12, 48);
      g.closePath();
      g.fillPath();
      g.fillStyle(0x387edb, 0.75);
      g.beginPath();
      g.moveTo(29, 2);
      g.lineTo(46, 48);
      g.lineTo(29, 54);
      g.closePath();
      g.fillPath();
    });
  }

  makeStar() {
    this.tex('star', 12, 12, (g) => {
      g.fillStyle(HEX.white, 1);
      g.fillCircle(6, 6, 2.2);
      g.fillStyle(HEX.white, 0.5);
      g.fillCircle(6, 6, 5);
    });
  }

  makeSpark() {
    this.tex('spark', 16, 16, (g) => {
      g.fillStyle(HEX.white, 1);
      g.fillCircle(8, 8, 4);
      g.fillStyle(HEX.white, 0.35);
      g.fillCircle(8, 8, 8);
    });
  }

  makeGlow() {
    const s = 128;
    this.tex('glow', s, s, (g) => {
      for (let r = 60; r > 0; r -= 6) {
        g.fillStyle(0xffffff, 0.04);
        g.fillCircle(s / 2, s / 2, r);
      }
    });
  }

  makeRestaPortal() {
    const s = 200;
    this.tex('portal_resta', s, s, (g) => {
      g.lineStyle(20, 0xe8dcc0, 1);
      g.strokeCircle(s / 2, s / 2, 80);
      g.lineStyle(10, 0x63c96b, 0.9);
      g.strokeCircle(s / 2, s / 2, 64);
      g.lineStyle(8, 0x63e8ff, 0.95);
      g.strokeCircle(s / 2, s / 2, 48);
      g.fillStyle(0x76e9ff, 0.3);
      g.fillCircle(s / 2, s / 2, 42);
      g.fillStyle(0xf5edd9, 1);
      g.fillRoundedRect(92, 42, 16, 9, 4);
    });
  }

  makeDivisionPlatform() { this.tex('platform_division',240,64,g=>{g.fillStyle(0x173b64,1);g.fillRoundedRect(0,7,240,57,17);g.fillStyle(0x092347,1);g.fillRoundedRect(6,21,228,42,13);g.fillStyle(0x7df6ff,1);g.fillRoundedRect(0,0,240,12,6);g.fillStyle(0xd9ffff,.65);g.fillRoundedRect(10,2,220,4,2);g.fillStyle(0xb8a5ff,.7);for(let i=0;i<4;i++)g.fillCircle(38+i*56,42,7);}); }
  makeDivisionCrystal() { this.tex('crystal_division',54,64,g=>{g.fillStyle(0x8cffff,.2);g.fillCircle(27,32,29);g.fillStyle(0xd9fbff,1);g.beginPath();g.moveTo(27,2);g.lineTo(51,26);g.lineTo(27,62);g.lineTo(3,26);g.closePath();g.fillPath();g.fillStyle(0x8c72ff,.72);g.beginPath();g.moveTo(27,2);g.lineTo(27,61);g.lineTo(10,27);g.closePath();g.fillPath();g.fillStyle(0xffffff,.92);g.beginPath();g.moveTo(27,4);g.lineTo(39,26);g.lineTo(27,35);g.lineTo(16,26);g.closePath();g.fillPath();}); }
  makeMultiplicationPortal() {
    const s = 200;
    this.tex('portal_multiplicacion', s, s, (g) => {
      g.lineStyle(14, 0xffdf70, 0.92);
      g.strokeCircle(s / 2, s / 2, 78);
      g.lineStyle(10, 0xffa928, 0.94);
      g.strokeCircle(s / 2, s / 2, 58);
      g.lineStyle(8, 0xffc947, 0.95);
      g.strokeCircle(s / 2, s / 2, 40);
      g.fillStyle(0xffedaa, 0.24);
      g.fillCircle(s / 2, s / 2, 34);
    });
  }

  makeDivisionPortal() { this.tex('portal_division',200,200,g=>{g.fillStyle(0x57ecff,.18);g.fillCircle(100,100,88);g.lineStyle(15,0x79f7ff,.9);g.strokeCircle(100,100,78);g.lineStyle(10,0x8575ff,.88);g.strokeCircle(100,100,57);g.lineStyle(7,0xe1fbff,.9);g.strokeCircle(100,100,37);g.fillStyle(0x69eaff,.34);g.fillCircle(100,100,31);}); }
  makePortal() {
    const s = 200;
    this.tex('portal', s, s, (g) => {
      g.lineStyle(14, HEX.violet, 0.9);
      g.strokeCircle(s / 2, s / 2, 78);
      g.lineStyle(10, HEX.magenta, 0.9);
      g.strokeCircle(s / 2, s / 2, 58);
      g.lineStyle(8, HEX.cyan, 0.9);
      g.strokeCircle(s / 2, s / 2, 40);
      g.fillStyle(HEX.white, 0.25);
      g.fillCircle(s / 2, s / 2, 34);
    });
  }
}
